import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { usePassCodeur } from '@/lib/pass-codeur-context';
import CanvasPlay from '@/components/CanvasPlay';
import { getPublicProjectsByAlias, getPublicProject, incrementViewCount } from '@/lib/projects-service';
import { useToast } from '@/lib/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Play as PlayIcon, ArrowLeft, Calendar, Clock, Eye, Code, Github, RefreshCw, ImageOff, Crown } from 'lucide-react';
import AliasModal from '../components/AliasModal';
import SEO from '../components/SEO';

// Code initial par défaut au cas où aucun projet n'est trouvé
const initialCode = `/**
 * @description Erreur Jeu non trouvé
 */

function setup() {
  createCanvas(600, 600);
  textAlign(CENTER, CENTER);
}

function draw() {
  background(220);
  textSize(32);
  fill(0);
  text("Aucun projet trouvé", width/2, height/2);
}`;

// Fonction pour extraire la description du code
const extractDescriptionFromCode = (code) => {
  try {
    // Chercher un bloc de commentaire JSDoc
    const jsdocMatch = code.match(/\/\*\*([\s\S]*?)\*\//);
    
    if (jsdocMatch) {
      // Extraire la description
      const descMatch = jsdocMatch[1].match(/@description\s+([\s\S]*?)(?=@|$)/);
      if (descMatch) {
        // Nettoyer la description en supprimant les * au début de chaque ligne
        return descMatch[1]
          .trim()
          .split('\n')
          .map(line => line.replace(/^\s*\*\s*/, ''))
          .join('\n');
      }
    }
    
    return "Aucune description disponible pour ce jeu.";
  } catch (error) {
    console.error("Erreur lors de l'extraction de la description:", error);
    return "Aucune description disponible pour ce jeu.";
  }
};

const Play = () => {
  const { alias, projectId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { currentUser, userData } = useAuth();
  const { hasAccessToCourses } = usePassCodeur();
  
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentCode, setCurrentCode] = useState(initialCode);
  const [isLoading, setIsLoading] = useState(true);
  const [errorData, setErrorData] = useState(null);
  const [gameDescription, setGameDescription] = useState("");
  const [showAliasModal, setShowAliasModal] = useState(false);
  const canvasRef = useRef(null);
  const sketchLaunchedRef = useRef(false);
  const projectsCacheRef = useRef({});
  const viewedProjectsRef = useRef(new Set()); // Pour suivre les projets déjà vus
  
  // Extraire la description lorsque le code change
  useEffect(() => {
    if (currentCode) {
      const description = extractDescriptionFromCode(currentCode);
      setGameDescription(description);
    }
  }, [currentCode]);
  
  // Réinitialiser le flag de lancement quand le code ou le projet change
  useEffect(() => {
    sketchLaunchedRef.current = false;
  }, [currentCode, selectedProject]);
  
  // Effet pour vérifier si l'utilisateur a besoin de choisir un alias
  useEffect(() => {
    if (currentUser && userData && !userData.alias && !alias && hasAccessToCourses()) {
      setShowAliasModal(true);
    }
  }, [currentUser, userData, alias, hasAccessToCourses]);
  
  // Charger les projets publics de l'utilisateur
  useEffect(() => {
    // Référence pour éviter les mises à jour si le composant est démonté
    let isMounted = true;
    
    const loadProjects = async () => {
      if (!alias) return;
      
      setIsLoading(true);
      try {
        // Si on navigue directement vers un projet spécifique, charger uniquement ce projet
        if (projectId) {
          try {
            // Vérifier si le projet existe déjà dans le cache
            const cachedProjects = projectsCacheRef.current[alias] || [];
            const cachedProject = cachedProjects.find(p => p.slug === projectId);
            
            if (cachedProject && cachedProject.code) {
              // Utiliser le projet en cache
              if (isMounted) {
                setSelectedProject(cachedProject);
                setCurrentCode(cachedProject.code);
                setIsLoading(false);
              }
            } else {
              // Charger le projet depuis Firebase
              const project = await getPublicProject(projectId, alias);
              if (isMounted) {
                setSelectedProject(project);
                setCurrentCode(project.code);
                
                // Stocker uniquement ce projet dans le cache
                const updatedCache = [...cachedProjects];
                const existingIndex = updatedCache.findIndex(p => p.id === project.id);
                if (existingIndex >= 0) {
                  updatedCache[existingIndex] = project;
                } else {
                  updatedCache.push(project);
                }
                projectsCacheRef.current[alias] = updatedCache;
              }
            }
          } catch (error) {
            console.error('Erreur lors du chargement du projet spécifique:', error);
            if (isMounted) {
              toast({
                title: 'Projet non trouvé',
                description: 'Le projet demandé n\'existe pas ou n\'est pas public.',
                variant: 'destructive',
              });
              
              // Rediriger vers la liste des projets de l'utilisateur
              navigate(`/play/${alias}`);
            }
          }
        } 
        // Sinon, charger tous les projets publics
        else {
          // Vérifier si les projets sont déjà en cache et s'il y en a plus d'un
          if (projectsCacheRef.current[alias]?.length > 0) {
            // Utiliser les projets en cache
            if (isMounted) {
              setProjects(projectsCacheRef.current[alias]);
              setIsLoading(false);
              
              // Si aucun projet n'est sélectionné mais qu'il y a des projets, sélectionner le premier
              if (!selectedProject && projectsCacheRef.current[alias].length > 0) {
                const firstProject = projectsCacheRef.current[alias][0];
                setSelectedProject(firstProject);
                setCurrentCode(firstProject.code || initialCode);
              }
            }
          } else {
            // Charger les projets depuis Firebase
            const publicProjects = await getPublicProjectsByAlias(alias);
            if (isMounted) {
              setProjects(publicProjects);
              // Mettre en cache les projets
              projectsCacheRef.current[alias] = publicProjects;
              
              // Si aucun projectId n'est spécifié mais qu'il y a des projets, sélectionner le premier
              if (publicProjects.length > 0) {
                setSelectedProject(publicProjects[0]);
                setCurrentCode(publicProjects[0].code);
              }
            }
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des projets:', error);
        if (isMounted) {
          toast({
            title: 'Erreur',
            description: 'Impossible de charger les projets de cet utilisateur.',
            variant: 'destructive',
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    
    loadProjects();
    
    // Nettoyage pour éviter les mises à jour sur un composant démonté
    return () => {
      isMounted = false;
    };
  }, [alias, projectId]); // Supprimer navigate et toast des dépendances
  
  // Effet pour exécuter le sketch automatiquement
  useEffect(() => {
    if (!selectedProject || !canvasRef.current || !projectId) return;
    
    // Réinitialiser le flag de lancement quand le projet change
    sketchLaunchedRef.current = false;
    
    const timer = setTimeout(() => {
      if (canvasRef.current?.runSketch && !sketchLaunchedRef.current) {
        canvasRef.current.runSketch();
        sketchLaunchedRef.current = true;
      }
    }, 0);
    
    return () => clearTimeout(timer);
  }, [selectedProject?.id, projectId]); // N'utiliser que l'ID du projet comme dépendance
  
  // Désactiver le défilement du body uniquement quand on joue à un jeu spécifique
  useEffect(() => {
    // Sauvegarder le style de défilement actuel
    const originalStyle = document.body.style.overflow;
    
    // Désactiver le défilement du body si on regarde un jeu spécifique
    // (le scroll sera géré par la zone de contenu interne)
    if (selectedProject && projectId) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = originalStyle;
    }
    
    // Rétablir le style original lors du démontage du composant
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, [selectedProject, projectId]);
  
  // Formatter la date pour l'affichage
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      return 'Date inconnue';
    }
  };
  
  // Formatter la date relative pour l'affichage
  const formatRelativeDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInSeconds = (now - date) / 1000;

      if (diffInSeconds < 60) {
        return `il y a ${Math.round(diffInSeconds)} secondes`;
      } else if (diffInSeconds < 3600) {
        const minutes = Math.round(diffInSeconds / 60);
        return `il y a ${minutes} minute${minutes > 1 ? 's' : ''}`;
      } else if (diffInSeconds < 86400) {
        const hours = Math.round(diffInSeconds / 3600);
        return `il y a ${hours} heure${hours > 1 ? 's' : ''}`;
      } else {
        const days = Math.round(diffInSeconds / 86400);
        return `il y a ${days} jour${days > 1 ? 's' : ''}`;
      }
    } catch (e) {
      return '';
    }
  };
  
  // Effet pour incrémenter le compteur de vues quand un projet est consulté
  useEffect(() => {
    // S'assurer qu'on a un projet valide et qu'on est sur la vue d'un jeu spécifique
    if (!selectedProject?.id || !projectId) return;
    
    // Réinitialiser le compteur de vues pour ce projet quand on entre sur la page
    viewedProjectsRef.current.delete(selectedProject.id);
    
    // Utiliser un timeout pour s'assurer que c'est le projet final qui est affiché
    const timeoutId = setTimeout(() => {
      const incrementView = async () => {
        // Vérifier à nouveau que le projet est toujours le même
        if (!selectedProject?.id || selectedProject.slug !== projectId) return;
        
        const projectKey = selectedProject.id;
        // Vérifier si le projet a déjà été vu dans cette session
        if (viewedProjectsRef.current.has(projectKey)) return;
        
        // Marquer comme vu
        viewedProjectsRef.current.add(projectKey);
        
        try {
          // Incrémenter le compteur dans Firebase
          await incrementViewCount(projectKey);
          
          // Mettre à jour l'affichage local
          setSelectedProject(prev => {
            if (prev?.id !== projectKey) return prev; // Vérifier que le projet n'a pas changé
            return {
              ...prev,
              viewCount: (prev.viewCount || 0) + 1
            };
          });
          
          // Mettre à jour le cache
          if (alias) {
            const cachedProjects = [...(projectsCacheRef.current[alias] || [])];
            const updatedCache = cachedProjects.map(p => 
              p.id === projectKey 
                ? { ...p, viewCount: (p.viewCount || 0) + 1 } 
                : p
            );
            projectsCacheRef.current[alias] = updatedCache;
          }
        } catch (error) {
          console.error("Erreur lors de l'incrémentation du compteur de vues:", error);
        }
      };
      
      incrementView();
    }, 200); // Petit délai pour s'assurer que le projet final est affiché
    
    // Nettoyer le timeout si l'effet est déclenché à nouveau
    return () => clearTimeout(timeoutId);
  }, [selectedProject?.id, projectId]);
  
  // Fonction pour construire l'URL de l'icône du projet
  const getProjectIconUrl = (project) => {
    if (!project?.userId || !project?.id) return null;
    return `/assets/${project.userId}/${project.id}/icon.jpg`;
  };

  // Fonction pour simuler une pression de touche
  const simulateKeyPress = (key, code) => {
    const canvas = document.querySelector('canvas');
    if (!canvas) return;
    
    // Mapper les codes de touches vers les constantes p5.js
    let keyCode = 0;
    switch (code) {
      case 'ArrowLeft': keyCode = 37; break;  // LEFT_ARROW
      case 'ArrowUp': keyCode = 38; break;    // UP_ARROW
      case 'ArrowRight': keyCode = 39; break; // RIGHT_ARROW
      case 'ArrowDown': keyCode = 40; break;  // DOWN_ARROW
      case 'Space': keyCode = 32; break;      // SPACE
      case 'ShiftLeft': keyCode = 16; break;  // SHIFT
    }
    
    // Créer les événements keydown et keyup avec keyCode
    const keyDownEvent = new KeyboardEvent('keydown', {
      key: key,
      code: code,
      keyCode: keyCode,
      which: keyCode,
      bubbles: true,
      cancelable: true
    });
    
    const keyUpEvent = new KeyboardEvent('keyup', {
      key: key,
      code: code,
      keyCode: keyCode,
      which: keyCode,
      bubbles: true,
      cancelable: true
    });
    
    // Dispatcher les événements sur le canvas et le document
    canvas.dispatchEvent(keyDownEvent);
    document.dispatchEvent(keyDownEvent);
    
    // Simuler un court délai de pression puis relâchement
    setTimeout(() => {
      canvas.dispatchEvent(keyUpEvent);
      document.dispatchEvent(keyUpEvent);
    }, 100);
  };
  
  // Si aucun alias n'est spécifié et que l'utilisateur est connecté
  if (!alias && currentUser) {
    return (
      <>
        <SEO 
          title="Vos projets publics - Mon Premier Jeu"
          description="Consultez et gérez vos projets de jeux vidéo publics. Partagez vos créations avec la communauté Mon Premier Jeu."
          keywords="projets publics, partage jeux, communauté, créations utilisateur, jeux partagés"
          url="/play"
          type="webapp"
        />
        <AliasModal 
          isOpen={showAliasModal} 
          onClose={() => {
            setShowAliasModal(false);
            if (userData?.alias) {
              navigate(`/play/${userData.alias}`);
            } else {
              navigate('/');
            }
          }} 
        />
        <div className="container mx-auto px-4 py-8 text-center">
          <h1 className="text-3xl font-bold mb-12">Vos Projets</h1>
          
          {!currentUser ? (
            <div className="border border-border rounded-lg p-8 bg-card max-w-md mx-auto shadow-lg">
              <div className="flex flex-col items-center gap-6">
                <div className="text-center">
                  <h3 className="text-2xl font-semibold mb-2">Compte requis</h3>
                  <p className="text-muted-foreground">
                    Vous devez créer un compte gratuit ou vous connecter pour créer et partager vos jeux.
                  </p>
                </div>
                <Button 
                  onClick={() => navigate('/login')}
                  className="pass-codeur-btn"
                  size="lg"
                >
                  Créer un compte / Se connecter
                </Button>
              </div>
            </div>
          ) : userData?.alias ? (
            <div className="py-8">
              <Button 
                onClick={() => navigate(`/play/${userData.alias}`)}
                size="lg"
              >
                Voir mes projets publics
              </Button>
            </div>
          ) : (
            <p className="text-xl">Pour partager et consulter vos projets, vous devez d'abord définir un alias.</p>
          )}
        </div>
      </>
    );
  }
  
  // Si aucun alias n'est spécifié et que l'utilisateur n'est pas connecté
  if (!alias && !currentUser) {
    return (
      <>
        <SEO 
          title="Découvrir les jeux de la communauté - Mon Premier Jeu"
          description="Découvrez les jeux créés par la communauté Mon Premier Jeu. Jouez aux créations des autres utilisateurs et laissez-vous inspirer."
          keywords="jeux communauté, découvrir jeux, créations utilisateurs, jouer en ligne, inspiration"
          url="/play"
          type="webapp"
        />
        <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-3xl font-bold mb-12">Vos Projets</h1>
        
        <div className="border border-border rounded-lg p-8 bg-card max-w-md mx-auto shadow-lg">
          <div className="flex flex-col items-center gap-6">
            <div className="text-center">
              <h3 className="text-2xl font-semibold mb-2">Compte requis</h3>
              <p className="text-muted-foreground">
                Vous devez créer un compte gratuit ou vous connecter pour créer et partager vos jeux.
              </p>
            </div>
            <Button 
              onClick={() => navigate('/login')}
              className="pass-codeur-btn"
              size="lg"
            >
              Créer un compte / Se connecter
            </Button>
          </div>
        </div>
      </div>
      </>
    );
  }
  
  // Si un projet est sélectionné, afficher le jeu
  if (selectedProject && projectId) {
    return (
      <>
        <SEO 
          title={`${selectedProject.name} par ${alias} - Jouer en ligne - Mon Premier Jeu`}
          description={`Jouez à ${selectedProject.name}, un jeu créé par ${alias} avec Mon Premier Jeu. ${gameDescription || 'Découvrez ce jeu créé avec p5.js.'}`}
          keywords={`${selectedProject.name}, jeu ${alias}, jouer en ligne, p5.js, jeu navigateur, ${gameDescription ? 'jeu interactif' : 'création communauté'}`}
          url={`/play/${alias}/${projectId}`}
          type="game"
          image={`/assets/${selectedProject.userId}/${selectedProject.id}/icon.jpg`}
        />
        <div className="fixed inset-0 pt-16 flex flex-col">
        {/* Contenu scrollable avec le canvas et la description */}
        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto">
            <div className="flex flex-col items-center">
              {/* Espace entre navbar et barre */}
              <div className="h-6 flex-shrink-0"></div>
              
              {/* En-tête avec les contrôles et infos - barre centrée avec largeur limitée */}
              <div className="w-full max-w-2xl px-4 flex-shrink-0 mb-6">
                <div className="w-full bg-background/60 backdrop-blur-sm rounded-lg px-4 py-3 flex items-center justify-between">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => navigate(`/play/${alias}`)}
                    className="flex items-center"
                  >
                    <ArrowLeft className="h-4 w-4 sm:mr-1" />
                    <span className="hidden sm:inline">Retour</span>
                  </Button>
                  
                  <div className="text-center mx-2">
                    <h1 className="text-xl font-bold">{selectedProject.name}</h1>
                    <div className="flex items-center justify-center text-sm text-muted-foreground">
                      <span>Par {alias}</span>
                      <span className="mx-2">•</span>
                      <div className="flex items-center">
                        <Eye className="h-3.5 w-3.5 mr-1" />
                        <span>{selectedProject.viewCount || 0} vues</span>
                      </div>
                    </div>
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => canvasRef.current?.runSketch()}
                    className="flex items-center"
                  >
                    <RefreshCw className="h-4 w-4 sm:mr-1" />
                    <span className="hidden sm:inline">Relancer</span>
                  </Button>
                </div>
              </div>
              {/* Canvas */}
              <div className="w-full max-w-6xl px-4 mb-6">
              <CanvasPlay 
                code={currentCode}
                onError={setErrorData}
                ref={canvasRef}
                workdir={selectedProject.userId+"/"+selectedProject.id}
              />
            </div>
              
              {/* Boutons virtuels de clavier - Visible uniquement sur mobile */}
              <div className="block md:hidden w-full max-w-lg px-4 mb-6">
              <div className="flex justify-center items-center gap-12">
                {/* Groupe des flèches directionnelles */}
                <div className="flex flex-col items-center gap-3">
                  {/* Ligne du haut - Flèche haut */}
                  <div className="flex justify-center">
                    <button
                      onClick={() => simulateKeyPress('ArrowUp', 'ArrowUp')}
                      className="bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg w-16 h-16 flex items-center justify-center shadow-md active:bg-gray-100 dark:active:bg-gray-700 transition-colors"
                      style={{ userSelect: 'none' }}
                    >
                      <svg className="w-8 h-8 text-gray-700 dark:text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M7 14l5-5 5 5z"/>
                      </svg>
                    </button>
                  </div>
                  
                  {/* Ligne du milieu - Gauche, Bas, Droite */}
                  <div className="flex justify-center gap-3">
                    <button
                      onClick={() => simulateKeyPress('ArrowLeft', 'ArrowLeft')}
                      className="bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg w-16 h-16 flex items-center justify-center shadow-md active:bg-gray-100 dark:active:bg-gray-700 transition-colors"
                      style={{ userSelect: 'none' }}
                    >
                      <svg className="w-8 h-8 text-gray-700 dark:text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"/>
                      </svg>
                    </button>
                    
                    <button
                      onClick={() => simulateKeyPress('ArrowDown', 'ArrowDown')}
                      className="bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg w-16 h-16 flex items-center justify-center shadow-md active:bg-gray-100 dark:active:bg-gray-700 transition-colors"
                      style={{ userSelect: 'none' }}
                    >
                      <svg className="w-8 h-8 text-gray-700 dark:text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M7 10l5 5 5-5z"/>
                      </svg>
                    </button>
                    
                    <button
                      onClick={() => simulateKeyPress('ArrowRight', 'ArrowRight')}
                      className="bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg w-16 h-16 flex items-center justify-center shadow-md active:bg-gray-100 dark:active:bg-gray-700 transition-colors"
                      style={{ userSelect: 'none' }}
                    >
                      <svg className="w-8 h-8 text-gray-700 dark:text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                      </svg>
                    </button>
                  </div>
                </div>
                
                {/* Groupe SHIFT et ESPACE à droite */}
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => simulateKeyPress('Shift', 'ShiftLeft')}
                    className="bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg w-16 h-16 flex items-center justify-center shadow-md active:bg-gray-100 dark:active:bg-gray-700 transition-colors"
                    style={{ userSelect: 'none' }}
                  >
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">SHIFT</span>
                  </button>
                  
                  <button
                    onClick={() => simulateKeyPress(' ', 'Space')}
                    className="bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg w-16 h-16 flex items-center justify-center shadow-md active:bg-gray-100 dark:active:bg-gray-700 transition-colors"
                    style={{ userSelect: 'none' }}
                  >
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">ESPACE</span>
                  </button>
                </div>
              </div>
            </div>
              
              {/* Description du jeu extraite des commentaires du code */}
              {gameDescription && (
                <div className="w-full max-w-2xl px-6">
                  <div className="bg-muted/30 rounded-lg p-4 text-sm">
                    <h3 className="font-semibold mb-2">À propos de ce jeu</h3>
                    <p className="text-muted-foreground whitespace-pre-line">
                      {gameDescription}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </>
    );
  }
  
  // Sinon, afficher la liste des projets
  return (
    <>
      <SEO 
        title={`Projets de ${alias} - Jeux de la communauté - Mon Premier Jeu`}
        description={`Découvrez tous les jeux créés par ${alias}. Jouez en ligne aux créations de ce membre de la communauté Mon Premier Jeu.`}
        keywords={`projets ${alias}, jeux ${alias}, créations utilisateur, jeux communauté, jouer en ligne`}
        url={`/play/${alias}`}
        type="profile"
      />
      <div className="container mx-auto px-4 py-8">
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : projects.length === 0 ? (
        <div className="text-center py-12 bg-muted/30 rounded-lg">
          <Code className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-2xl font-semibold mb-2">Aucun projet public</h2>
          <p className="text-muted-foreground mb-6">
            Cet utilisateur n'a pas encore partagé de projets publics.
          </p>
        </div>
      ) : (
        <>
          <header className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Projets de {alias}</h1>
          </header>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map(project => (
              <Card key={project.id} className="overflow-hidden flex flex-col hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <CardTitle>{project.name}</CardTitle>
                  <CardDescription className="flex items-center justify-between">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" />
                      <span title={formatDate(project.updatedAt)}>
                        Mis à jour {formatRelativeDate(project.updatedAt)}
                      </span>
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="h-3.5 w-3.5" />
                      <span>{project.viewCount || 0}</span>
                    </span>
                  </CardDescription>
                </CardHeader>
                <CardContent className="pb-0 flex-grow">
                  <div className="h-40 w-full bg-muted/30 rounded-md overflow-hidden flex items-center justify-center">
                      <img 
                        src={getProjectIconUrl(project)} 
                        alt={`Aperçu de ${project.name}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                        e.target.src = ''; // Vider la source en cas d'erreur
                        e.target.parentElement.innerHTML = '<PlayIcon className="h-12 w-12 text-muted-foreground/50" />';
                      }}
                    />
                  </div>
                </CardContent>
                <CardFooter className="pt-4">
                  <Button 
                    className="w-full" 
                    onClick={() => navigate(`/play/${alias}/${project.slug}`)}
                  >
                    <PlayIcon className="h-4 w-4 mr-2" />
                    Jouer
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
    </>
  );
};

export default Play; 